<template>
  <div class="ncrm-privacy-notices d-flex flex-column ga-4">
    <div v-if="returnTo" class="d-flex">
      <v-btn variant="text" prepend-icon="mdi-arrow-left" :to="returnTo">{{ $t("ncrm.contacts.dataProtection.workspace.purposes.back") }}</v-btn>
    </div>
    <div>
      <h3 class="text-subtitle-1 font-weight-bold">{{ $t(`${copyKey}.title`) }}</h3>
      <p class="text-body-2 text-medium-emphasis mb-0">{{ $t(`${copyKey}.subtitle`) }}</p>
    </div>

    <div class="ncrm-privacy-notices__toolbar">
      <v-select :model-value="purposeId" :items="purposeItems" :label="$t(`${copyKey}.purpose`)"
        hide-details :loading="purposesLoading" @update:model-value="patchQuery({ purpose: $event })" />
      <NcrmSegmentedControl :model-value="locale" :items="localeItems" :aria-label="$t(`${copyKey}.locale`)"
        @update:model-value="patchQuery({ locale: $event })" />
      <NcrmPrivacyPurposeLink :purpose-id="purposeId" />
    </div>

    <v-alert v-if="errorMessage" type="error">
      {{ errorMessage }}
      <template #append><v-btn variant="text" @click="retry">{{ $t(`${copyKey}.retry`) }}</v-btn></template>
    </v-alert>
    <v-alert v-else-if="!purposeId" type="info">{{ $t(`${copyKey}.pickPurpose`) }}</v-alert>
    <template v-else>
      <div class="d-flex align-center justify-space-between flex-wrap ga-2">
        <span class="text-body-2 text-medium-emphasis">{{ pairSummary }}</span>
        <v-btn v-if="primaryAction" color="primary" :prepend-icon="primaryAction.icon" @click="primaryAction.run()">
          {{ $t(`${copyKey}.actions.${primaryAction.key}`) }}
        </v-btn>
      </div>
      <v-data-table
        :headers="headers"
        :items="notices"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
        :no-data-text="$t(`${copyKey}.empty`)"
        :row-props="() => ({ style: 'cursor: pointer' })"
        item-value="id"
        @click:row="(_, { item }) => openDrawer(item)"
      >
        <template #[`item.version`]="{ item }"><span class="font-weight-medium">v{{ item.version }}</span></template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="statusColors[item.status]">{{ $t(`${drawerKey}.status.${item.status}`) }}</v-chip>
        </template>
        <template #[`item.published_at`]="{ item }">{{ formatDateTime(item.published_at) }}</template>
        <template #[`item.consent_text`]="{ item }">
          <span class="ncrm-privacy-notices__excerpt">{{ item.consent_text }}</span>
        </template>
      </v-data-table>
    </template>

    <NcrmPrivacyNoticeDrawer v-model="drawerOpen" :notice="selected" :purpose-id="purposeId" :purpose-name="purposeName"
      :locale="locale" :can-create-next="Boolean(pair.nextSource) && selected?.id === pair.nextSource?.id"
      :read-only="purposeRetired" @saved="afterSave" />
    <v-snackbar v-model="snackbar" color="success" :timeout="3500">{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import NcrmPrivacyNoticeDrawer from "./NcrmPrivacyNoticeDrawer.vue";
import NcrmPrivacyPurposeLink from "./NcrmPrivacyPurposeLink.vue";
import { fetchPurposeCatalog } from "./privacyPurposeCatalogApi.mjs";
import { safeReturnTo } from "./privacyPurposeModel.mjs";
import { NOTICE_LOCALES, fetchNotices, noticePairState } from "./privacyNoticeApi.mjs";

export default {
  name: "NcrmPrivacyNoticesSection",
  components: { NcrmPrivacyNoticeDrawer, NcrmPrivacyPurposeLink, NcrmSegmentedControl },
  data: () => ({
    purposes: [], purposesLoading: false, notices: [], loading: false, errorMessage: "", requestSequence: 0,
    drawerOpen: false, selected: null, snackbar: false, snackbarText: "",
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace.notices"; },
    // Venit din alt ecran (de ex. `Forme externe`, S5.1): Back readuce contextul sursă.
    returnTo() { return safeReturnTo(this.$route.query.returnTo); },
    drawerKey() { return `${this.copyKey}.drawer`; },
    statusColors() { return { draft: "info", published: "success", retired: undefined }; },
    // Scopul și limba stau în URL, ca deep-linkul și Back să restaureze perechea.
    purposeId() {
      const id = Number(this.$route.query.purpose);
      return this.purposes.some((purpose) => purpose.id === id) ? id : null;
    },
    locale() { return NOTICE_LOCALES.includes(this.$route.query.locale) ? this.$route.query.locale : "ro"; },
    purposeName() { return this.purposes.find((purpose) => purpose.id === this.purposeId)?.name || ""; },
    // Scopurile retrase nu mai primesc texte, dar istoricul lor rămâne consultabil.
    purposeItems() {
      return this.purposes.map((purpose) => ({
        value: purpose.id,
        title: purpose.status === "active" ? purpose.name
          : `${purpose.name} · ${this.$t(`ncrm.contacts.dataProtection.workspace.purposes.drawer.status.${purpose.status}`)}`,
      }));
    },
    localeItems() { return NOTICE_LOCALES.map((value) => ({ value, label: value.toUpperCase() })); },
    pair() { return noticePairState(this.notices); },
    // Orice schimbare a perechii din URL (selector, Back) reîncarcă versiunile.
    pairKey() { return `${this.purposeId}:${this.locale}`; },
    purposeRetired() { return this.purposes.find((purpose) => purpose.id === this.purposeId)?.status === "retired"; },
    // O singură acțiune principală, după starea perechii.
    primaryAction() {
      if (this.purposeRetired || this.loading) return null;
      if (this.pair.draft) return { key: "continueDraft", icon: "mdi-pencil", run: () => this.openDrawer(this.pair.draft) };
      if (this.pair.nextSource) return { key: "nextVersion", icon: "mdi-content-copy", run: () => this.openDrawer(this.pair.nextSource) };
      return { key: "createDraft", icon: "mdi-plus", run: () => this.openDrawer(null) };
    },
    pairSummary() {
      if (this.purposeRetired) return this.$t(`${this.copyKey}.retiredPurpose`);
      const published = this.pair.published;
      return published
        ? this.$t(`${this.copyKey}.currentPublished`, { version: published.version, date: this.formatDateTime(published.published_at) })
        : this.$t(`${this.copyKey}.nonePublished`);
    },
    headers() {
      return ["version", "status", "published_at", "consent_text"].map((key) => ({
        key, title: this.$t(`${this.copyKey}.columns.${key}`), sortable: false,
      }));
    },
  },
  watch: {
    pairKey: { handler() { this.load(); } },
  },
  created() { this.loadPurposes(); },
  methods: {
    patchQuery(changes) {
      this.$router.replace({ query: { ...this.$route.query, ...changes } });
    },
    // Fără scopuri nu există pereche de încărcat: retry-ul reia de la catalog.
    retry() { return this.purposes.length ? this.load() : this.loadPurposes(); },
    async loadPurposes() {
      this.purposesLoading = true; this.errorMessage = "";
      try {
        // Perechea devine validă când scopurile sosesc; `pairKey` pornește încărcarea.
        this.purposes = (await fetchPurposeCatalog()).sort((a, b) => (a.status === "active" ? 0 : 1) - (b.status === "active" ? 0 : 1)
          || a.name.localeCompare(b.name));
      } catch {
        this.errorMessage = this.$t(`${this.copyKey}.loadError`);
      } finally {
        this.purposesLoading = false;
      }
    },
    async load() {
      const sequence = ++this.requestSequence;
      if (!this.purposeId) { this.notices = []; this.loading = false; return; }
      this.loading = true; this.errorMessage = "";
      try {
        const notices = await fetchNotices({ purposeId: this.purposeId, locale: this.locale });
        if (sequence === this.requestSequence) this.notices = notices;
      } catch {
        if (sequence === this.requestSequence) { this.notices = []; this.errorMessage = this.$t(`${this.copyKey}.loadError`); }
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    openDrawer(notice) {
      this.selected = notice;
      this.drawerOpen = true;
    },
    afterSave(kind) {
      this.snackbarText = this.$t(`${this.copyKey}.saved.${kind}`);
      this.snackbar = true;
      this.load();
    },
    formatDateTime(value) {
      const date = new Date(value);
      if (!value || Number.isNaN(date.getTime())) return "—";
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.ncrm-privacy-notices { text-align: left; }
.ncrm-privacy-notices__toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.ncrm-privacy-notices__toolbar > .v-input { flex: 1 1 260px; max-width: 420px; }
.ncrm-privacy-notices__excerpt {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; max-width: 520px;
}
</style>
